import React, { useMemo, useState } from 'react';
import { IntelligenceShell, MonoNum } from '../components/intelligence/IntelligenceShell';
import { useIntelligenceReport, KpiCard, ChartCard, RangeControl, ExportMenu, ReportHeader, ReportSkeleton, downloadCsv, exportPdf, tooltipStyle, CARD } from '../components/intelligence/ReportKit';
import { AiSuggestion } from '../components/intelligence/AiSuggestion';
import { useOperatorLocale } from '../hooks/useOperatorLocale';
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';
import { Receipt } from 'lucide-react';

const CATEGORY_LABELS = {
  fuel: 'Fuel', maintenance: 'Maintenance', insurance: 'Insurance', supplies: 'Supplies',
  labor: 'Labor', salary: 'Employee Salary', rent: 'Rent', marketing: 'Marketing',
  utilities: 'Utilities', other: 'Other', uncategorized: 'Uncategorized',
};
const catLabel = (id) => CATEGORY_LABELS[id] || id;

export default function ExpensesReport() {
  const [months, setMonths] = useState(12);
  const { data, loading } = useIntelligenceReport('expenses', months);
  const { formatMoney } = useOperatorLocale();
  const s = data?.summary;

  const chartData = useMemo(() => (data?.monthly || []).map((m) => ({
    month: m.month.slice(5) + '/' + m.month.slice(2, 4),
    expenses: m.expenses,
    revenue: m.revenue,
  })), [data]);

  const exportCsv = () => {
    if (!data) return;
    downloadCsv([
      ['Month', 'Expenses', 'Revenue', 'Net', 'Expense count'],
      ...data.monthly.map((m) => [m.month, m.expenses, m.revenue, m.net, m.count]),
      [],
      ['Category', 'Amount', 'Share %', 'Count'],
      ...data.byCategory.map((c) => [catLabel(c.category), c.amount, c.share, c.count]),
    ], `expenses-${data.periodStart}-to-${data.periodEnd}.csv`);
  };

  const exportPdfDoc = () => {
    if (!data) return;
    exportPdf({
      filename: `expenses-${data.periodStart}-to-${data.periodEnd}.pdf`,
      title: 'Expense Tracker',
      subtitle: `Your expenses only · ${data.periodStart} to ${data.periodEnd}`,
      kpis: [
        ['Total expenses', formatMoney(s.totalExpenses)],
        ['Monthly average', formatMoney(s.monthlyAvg)],
        ['Largest category', s.largestCategory ? `${catLabel(s.largestCategory)} (${s.largestCategoryShare}%)` : '-'],
        ['Expense ratio', s.expenseRatio != null ? `${s.expenseRatio}% of revenue` : '-'],
        ['Net (revenue - expenses)', formatMoney(s.net)],
      ],
      tables: [
        {
          title: 'By category',
          head: ['Category', 'Amount', 'Share', 'Entries'],
          rows: data.byCategory.map((c) => [catLabel(c.category), formatMoney(c.amount), `${c.share}%`, c.count]),
        },
        {
          title: 'Monthly',
          head: ['Month', 'Expenses', 'Revenue', 'Net'],
          rows: data.monthly.map((m) => [m.month, formatMoney(m.expenses), formatMoney(m.revenue), formatMoney(m.net)]),
        },
      ],
    });
  };

  const AttributionTable = ({ title, rows, testid }) => (
    <ChartCard title={title} testid={testid} delay={480}>
      {rows.length === 0 ? (
        <p className="text-sm text-slate-400 py-2">Nothing attributed yet — tag expenses when you add them and they show up here.</p>
      ) : (
        <div className="space-y-3">
          {rows.slice(0, 6).map((r) => (
            <div key={r.id} className="flex items-center justify-between gap-3" data-testid={`${testid}-row-${r.id}`}>
              <div className="min-w-0">
                <div className="text-sm font-bold text-slate-800 truncate">{r.name}</div>
                <div className="text-[11px] text-slate-400">{r.count} {r.count === 1 ? 'entry' : 'entries'}</div>
              </div>
              <MonoNum className="text-sm font-extrabold text-slate-900 whitespace-nowrap">{formatMoney(r.amount)}</MonoNum>
            </div>
          ))}
        </div>
      )}
    </ChartCard>
  );

  return (
    <IntelligenceShell>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10" data-testid="expenses-report">
        <ReportHeader
          title="Expense Tracker"
          blurb={`Every dollar going out over the last ${months} months — by category, asset and crew — tracked against the revenue it supports.`}
          right={<><AiSuggestion report="expenses" months={months} /><RangeControl months={months} setMonths={setMonths} prefix="expenses" /><ExportMenu onCsv={exportCsv} onPdf={exportPdfDoc} testid="expenses-export" /></>}
        />

        {loading ? <ReportSkeleton /> : !data ? null : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
              <KpiCard delay={60} testid="kpi-total-expenses" label="Total expenses" value={formatMoney(s.totalExpenses)} sub={`${s.expenseCount} entries · last ${data.periodMonths} months`} />
              <KpiCard delay={120} testid="kpi-monthly-avg" label="Monthly average" value={formatMoney(s.monthlyAvg)} sub="spend per month" />
              <KpiCard delay={180} testid="kpi-largest-category" label="Largest category" value={s.largestCategory ? catLabel(s.largestCategory) : '—'} sub={s.largestCategoryShare != null ? `${s.largestCategoryShare}% of all spend` : 'no expenses logged yet'} />
              <KpiCard delay={240} testid="kpi-expense-ratio" label="Expense ratio" value={s.expenseRatio != null ? `${s.expenseRatio}%` : '—'} sub={`of revenue · ${formatMoney(s.net)} net`} />
            </div>

            <ChartCard delay={300} title="Monthly expenses vs revenue" testid="expenses-chart" className="mb-6">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
                    <Tooltip formatter={(v, name) => [formatMoney(v), name === 'expenses' ? 'Expenses' : 'Revenue']} contentStyle={tooltipStyle} />
                    <Legend wrapperStyle={{ fontSize: 12 }} formatter={(v) => (v === 'expenses' ? 'Expenses' : 'Revenue')} />
                    <Bar dataKey="expenses" fill="#F43F5E" radius={[6, 6, 0, 0]} maxBarSize={28} />
                    <Line type="monotone" dataKey="revenue" stroke="#2563EB" strokeWidth={2} dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </ChartCard>

            {data.byCategory.length === 0 ? (
              <div className={`${CARD} p-12 text-center intel-rise`} data-testid="expenses-empty">
                <div className="w-12 h-12 rounded-full bg-[#EBF2FF] flex items-center justify-center mx-auto mb-4">
                  <Receipt className="w-5 h-5 text-[#2563EB]" strokeWidth={1.75} />
                </div>
                <div className="font-bold text-slate-800 mb-1">No expenses logged yet</div>
                <p className="text-sm text-slate-400">Add expenses in Financials → Expenses (or snap a receipt) and this report fills itself.</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
                  <ChartCard delay={360} title="Spend by category" testid="expenses-by-category" className="lg:col-span-1">
                    <div className="space-y-3.5">
                      {data.byCategory.map((c) => (
                        <div key={c.category} data-testid={`expenses-category-${c.category}`}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-bold text-slate-800">{catLabel(c.category)}</span>
                            <MonoNum className="text-sm font-extrabold text-slate-900">{formatMoney(c.amount)}</MonoNum>
                          </div>
                          <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                            <div className="h-full rounded-full bg-[#F43F5E]" style={{ width: `${Math.max(c.share, 2)}%` }} />
                          </div>
                          <div className="text-[11px] text-slate-400 mt-1">{c.share}% · {c.count} {c.count === 1 ? 'entry' : 'entries'}</div>
                        </div>
                      ))}
                    </div>
                  </ChartCard>

                  <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <AttributionTable title="Spend by asset" rows={data.byAsset} testid="expenses-by-asset" />
                    <AttributionTable title="Spend by team member" rows={data.byTeamMember} testid="expenses-by-team" />
                  </div>
                </div>

                <div className={`${CARD} overflow-x-auto intel-rise`} style={{ animationDelay: '540ms' }} data-testid="expenses-top-table">
                  <div className="px-6 pt-5 text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">Largest expenses this period</div>
                  <table className="w-full text-sm mt-2">
                    <thead>
                      <tr className="border-b border-slate-100 text-left">
                        {['Expense', 'Category', 'Date', 'Amount'].map((h) => (
                          <th key={h} className="px-6 py-3 text-[11px] font-bold uppercase tracking-[0.12em] text-slate-400 whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.topExpenses.map((e) => (
                        <tr key={e.id} className="border-b border-slate-50 last:border-0 hover:bg-[#F7F9FC] transition-colors duration-150" data-testid={`expenses-top-row-${e.id}`}>
                          <td className="px-6 py-3.5 font-bold text-slate-900">{e.title}</td>
                          <td className="px-6 py-3.5 text-slate-600">{catLabel(e.category)}</td>
                          <td className="px-6 py-3.5"><MonoNum className="text-slate-600">{e.date || '—'}</MonoNum></td>
                          <td className="px-6 py-3.5"><MonoNum className="font-bold text-slate-900">{formatMoney(e.amount)}</MonoNum></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}
      </main>
    </IntelligenceShell>
  );
}
